import { Link } from "react-router-dom";
import {
  ArrowRight,
  Globe2,
  Play,
  Target,
  TrendingUp,
  Shield,
  Clock,
  Film,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { SpinningIco } from "@/components/brand/SpinningIco";

/**
 * /advertise - pitch page for brands that want to advertise in the
 * Whistll Reels feed. Kept narrow on purpose: in-feed vertical video
 * ads only, no banners, no interstitials, no court/team sponsorships
 * (those will live on their own pages when they exist). Exists so
 * inbound ad inquiries have a landing page with basic inventory info
 * and an email contact, instead of bouncing off the marketing home.
 */
export function AdvertisePage() {
  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border/60">
        <div
          aria-hidden
          className="pointer-events-none absolute left-1/2 top-16 -z-10 h-[520px] w-[520px] -translate-x-1/2 rounded-full opacity-25 blur-3xl"
          style={{
            backgroundImage:
              "radial-gradient(closest-side, hsl(var(--volt)), transparent 70%)",
          }}
        />
        <div className="mx-auto max-w-4xl px-6 pb-10 pt-20 text-center sm:pt-28">
          <div className="mb-6 flex justify-center">
            <SpinningIco size={76} />
          </div>
          <p className="mb-4 text-sm font-medium uppercase tracking-widest text-muted-foreground">
            Advertise on Whistll
          </p>
          <h1 className="text-4xl font-semibold leading-[1.05] tracking-tight sm:text-5xl">
            Reach hoopers{" "}
            <span className="text-volt">in their feed.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground">
            Whistll Reels is where players watch post-game clips from their
            city. Your brand can appear in that scroll with native
            15-second vertical video. Real audience, real engagement, zero
            interruption.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button asChild size="lg">
              <a href="mailto:hello@whistll.com?subject=Advertising%20enquiry">
                Get in touch
                <ArrowRight className="h-4 w-4" aria-hidden />
              </a>
            </Button>
            <Button asChild size="lg" variant="outline">
              <a href="#how-it-works">See how it works</a>
            </Button>
          </div>
        </div>
      </section>

      {/* Audience */}
      <section className="border-b border-border/60 bg-secondary/30">
        <div className="mx-auto max-w-5xl px-6 py-20">
          <div className="mb-10 text-center">
            <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">
              Who you're reaching
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-muted-foreground">
              Whistll is the pickup basketball app for the US, UK, Canada,
              Australia, and New Zealand.
            </p>
          </div>
          <div className="grid gap-6 sm:grid-cols-3">
            <Stat
              icon={<Target className="h-5 w-5" />}
              label="Core audience"
              value="16 – 35"
              sub="active basketball players"
            />
            <Stat
              icon={<Globe2 className="h-5 w-5" />}
              label="Live markets"
              value="5 countries"
              sub="US · UK · CA · AU · NZ"
            />
            <Stat
              icon={<Film className="h-5 w-5" />}
              label="Content context"
              value="Real games"
              sub="every clip is post-match footage"
            />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="border-b border-border/60">
        <div className="mx-auto max-w-5xl px-6 py-20">
          <div className="mb-12 text-center">
            <p className="mb-3 text-sm font-medium uppercase tracking-widest text-muted-foreground">
              The format
            </p>
            <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">
              In-feed Reels ads
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">
              One ad format, done properly. 15-second vertical video,
              inserted natively between player clips as they scroll.
            </p>
          </div>

          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            <Feature
              icon={<Play className="h-5 w-5" />}
              title="Native placement"
              body="Full-screen vertical, same shape as the clips around it. Swipe up to skip, like any feed."
            />
            <Feature
              icon={<Clock className="h-5 w-5" />}
              title="15 seconds"
              body="Short-form only. We don't sell longer formats - attention drops, results drop with it."
            />
            <Feature
              icon={<TrendingUp className="h-5 w-5" />}
              title="Every 10 clips"
              body="One ad slot per ten clips viewed, capped. We care more about the scroll feeling clean than about ad density."
            />
            <Feature
              icon={<Target className="h-5 w-5" />}
              title="Location targeting"
              body="Target by country, city, or radius around a venue. Useful for local brands, store openings, venue takeovers."
            />
            <Feature
              icon={<Shield className="h-5 w-5" />}
              title="Brand-safe by design"
              body="Every clip that sits next to your ad has gone through automated moderation and user reporting. No surprises."
            />
            <Feature
              icon={<Film className="h-5 w-5" />}
              title="Creative specs"
              body="Vertical 9:16, minimum 1080×1920, MP4/H.264, under 30MB. No voiceover required. We'll send the full creative brief on enquiry."
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section>
        <div className="mx-auto max-w-4xl px-6 py-20">
          <div className="relative overflow-hidden rounded-2xl border border-border bg-card p-8 sm:p-12">
            <div
              aria-hidden
              className="pointer-events-none absolute -right-24 -top-24 h-[320px] w-[320px] rounded-full opacity-40 blur-3xl"
              style={{
                backgroundImage:
                  "radial-gradient(closest-side, hsl(var(--volt) / 0.35), transparent 70%)",
              }}
            />
            <div className="relative flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
              <div className="max-w-xl">
                <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">
                  Interested? Tell us about your brand.
                </h2>
                <p className="mt-3 text-muted-foreground">
                  We'll send back audience data, creative specs, and
                  indicative pricing within 48 hours.
                </p>
              </div>
              <Button asChild size="lg">
                <a href="mailto:hello@whistll.com?subject=Advertising%20enquiry">
                  Email us
                  <ArrowRight className="h-4 w-4" aria-hidden />
                </a>
              </Button>
            </div>
          </div>

          <p className="mt-6 text-center text-xs text-muted-foreground">
            Running a venue or team? That's{" "}
            <Link
              to="/venues"
              className="underline underline-offset-2 hover:text-foreground"
            >
              a different path
            </Link>
            .
          </p>
        </div>
      </section>
    </>
  );
}

function Stat({
  icon,
  label,
  value,
  sub,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub: string;
}) {
  return (
    <div className="rounded-2xl border border-border bg-card p-6 text-center">
      <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-volt text-primary-foreground">
        {icon}
      </div>
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p className="mt-1 text-2xl font-black tracking-tight">{value}</p>
      <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>
    </div>
  );
}

function Feature({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-volt text-primary-foreground">
        {icon}
      </div>
      <h3 className="text-lg font-semibold">{title}</h3>
      <p className="text-sm leading-relaxed text-muted-foreground">{body}</p>
    </div>
  );
}
